import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAccountQuery } from "../../../features/auth/authApi";
import { uploadKycFile } from "../../../service/api";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { KycDocument } from "../../../types/kyc-document";
import { useNavigate } from "react-router-dom";

const isRecent = (file: File) => {
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return file.lastModified >= threeMonthsAgo.getTime();
};

const convertBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
  });

interface DocEntry {
  typeId: number;
  doc_id: string;
  typeName: string;
  files: File[];
  isNew: boolean;
}

const KycSupportingDocumentsForm = () => {
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();
  const [uploading, setUploading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<
    { id: number; doc_id: string; name: string }[]
  >([]);

  const formik = useFormik<{
    documents: DocEntry[];
  }>({
    initialValues: { documents: [] },
    onSubmit: async (values) => {
      try {
        setUploading(true);
        const newDocs = values.documents.filter((d) => d.isNew);
        for (const entry of newDocs) {
          for (const file of entry.files) {
            if (!isRecent(file)) continue;
            const base64 = await convertBase64(file);
            await uploadKycFile({
              base64,
              file_type: file.type,
              assign_id: entry.typeId.toString(),
              document_id: entry.doc_id,
            });
          }
        }
        if (account?.user.form3_json) {
          navigate("/separate-corp-declaration-view");
        } else navigate("/separate-corp-declaration");
      } catch (err) {
        console.error("Upload failed", err);
      } finally {
        setUploading(false);
      }
    },
  });

  useEffect(() => {
    const rawDocs: KycDocument[] =
      account?.user?.kycProgram?.kycDocuments || [];
    const filtered = rawDocs.filter(
      (d) =>
        ![
          "1",
          "2",
          "3",
          "4",
          "5",
          "300",
          "100",
          "basic-kyc-level",
          "basic-kyb-level",
        ].includes(d.doc_value)
    );
    const types = filtered.map((d) => ({
      id: d.id,
      doc_id: d.doc_id.toString(),
      name: d.name,
    }));
    setDocumentTypes(types);

    const uploaded = account?.user?.kycProgram?.uploadedDocWallets || [];
    const existing = uploaded
      .map((d) => {
        const match = types.find((t) => t.name === d.name);
        if (!match) return null;
        const fake = new File([""], d.fileName || "uploaded.pdf", {
          type: "application/pdf",
        });
        return {
          typeId: match.id,
          doc_id: match.doc_id,
          typeName: match.name,
          files: [fake],
          isNew: false,
        };
      })
      .filter((x): x is DocEntry => x !== null);

    formik.setFieldValue("documents", existing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const handleAdd = () => {
    formik.setFieldValue("documents", [
      ...formik.values.documents,
      { typeId: 0, doc_id: "", typeName: "", files: [], isNew: true },
    ]);
  };

  const handleTypeChange = (idx: number, newTypeId: number) => {
    const docs = [...formik.values.documents];
    const doc = docs[idx];
    if (!doc.isNew) return;
    const match = documentTypes.find((d) => d.id === newTypeId);
    if (!match) return;
    docs[idx] = {
      ...doc,
      typeId: match.id,
      doc_id: match.doc_id,
      typeName: match.name,
    };
    formik.setFieldValue("documents", docs);
  };

  const handleFileChange = (idx: number, fileList: FileList | null) => {
    if (!fileList) return;
    const docs = [...formik.values.documents];
    const doc = docs[idx];
    if (!doc.isNew) return;
    const valid = Array.from(fileList).filter(isRecent);
    docs[idx] = { ...doc, files: [...doc.files, ...valid] };
    formik.setFieldValue("documents", docs);
  };

  const handleRemoveFile = (dIdx: number, fIdx: number) => {
    const docs = [...formik.values.documents];
    const doc = docs[dIdx];
    if (!doc.isNew) return;
    const files = [...doc.files];
    files.splice(fIdx, 1);
    docs[dIdx] = { ...doc, files };
    formik.setFieldValue("documents", docs);
  };

  return (
    <Box mt={4}>
      <Container maxWidth="md">
        <form onSubmit={formik.handleSubmit}>
          <Card elevation={8} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" mb={1}>
                Upload Supporting Documents
              </Typography>
              <Typography variant="body2" mb={3} color="text.secondary">
                Please upload your documents now – you can always skip this step
                and add them later.
              </Typography>
              <Stack spacing={3}>
                {formik.values.documents.map((doc, idx) => (
                  <Box key={idx}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel id={`doc-type-label-${idx}`}>
                            Document Type
                          </InputLabel>
                          <Select
                            labelId={`doc-type-label-${idx}`}
                            label="Document Type"
                            value={doc.typeId || ""}
                            onChange={(e) =>
                              handleTypeChange(idx, Number(e.target.value))
                            }
                            disabled={!doc.isNew}
                          >
                            {documentTypes.map((d) => (
                              <MenuItem key={d.id} value={d.id}>
                                {d.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {doc.isNew && (
                          <Button
                            component="label"
                            fullWidth
                            variant="outlined"
                            sx={{ height: 56 }}
                          >
                            Upload File
                            <input
                              hidden
                              multiple
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleFileChange(idx, e.target.files)
                              }
                            />
                          </Button>
                        )}
                      </Grid>
                    </Grid>

                    {doc.files.length > 0 && (
                      <Stack spacing={1} mt={2}>
                        {doc.files.map((file, fIdx) => (
                          <Box
                            key={fIdx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 1,
                              bgcolor: "#f5f5f5",
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {file.name}
                            </Typography>
                            {doc.isNew && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(idx, fIdx)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}

                    <Divider sx={{ my: 3 }} />
                  </Box>
                ))}

                <Button variant="contained" onClick={handleAdd}>
                  + Add Document
                </Button>
              </Stack>

              <LoadingButton
                sx={{ mt: 4 }}
                type="submit"
                fullWidth
                variant="contained"
                loading={uploading}
              >
                Submit
              </LoadingButton>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default KycSupportingDocumentsForm;
